/**
 * <mineralia-size-guide>
 * Opens a native modal <dialog> (focus trap and Escape come from the platform), handles
 * keyboard tabs (arrows, Home, End) and closes when the backdrop is clicked.
 */
class MineraliaSizeGuide extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const dialog = this.#dialog;
    if (!dialog) return;

    this.querySelector('[data-size-guide-open]')?.addEventListener('click', this.#open, { signal });
    dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => dialog.close(), { signal });

    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) dialog.close();
      },
      { signal }
    );

    dialog.addEventListener(
      'close',
      () => {
        this.querySelector('[data-size-guide-open]')?.focus({ preventScroll: true });
      },
      { signal }
    );

    const tabs = [...dialog.querySelectorAll('[data-size-guide-tab]')];
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#select(tabs, tab), { signal });
      tab.addEventListener(
        'keydown',
        (event) => {
          const keys = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 };
          if (!(event.key in keys)) return;
          event.preventDefault();
          const next = tabs[(keys[event.key] + tabs.length) % tabs.length];
          this.#select(tabs, next);
          next.focus();
        },
        { signal }
      );
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #dialog() {
    const dialog = this.querySelector('[data-size-guide-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  #open = () => {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    dialog.querySelector('[role="tab"][aria-selected="true"]')?.focus({ preventScroll: true });
  };

  /**
   * @param {Element[]} tabs
   * @param {Element} selected
   */
  #select(tabs, selected) {
    tabs.forEach((tab) => {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls') || '')}`);
      if (panel instanceof HTMLElement) panel.hidden = !isSelected;
    });
  }
}

if (!customElements.get('mineralia-size-guide')) {
  customElements.define('mineralia-size-guide', MineraliaSizeGuide);
}
